import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import IoMdMenu from "react-icons/lib/io/android-menu";
import HasRolePermission from "components/shared/has-role-permission";
import UnallocateModal from "./modals/unallocate-modal";
// TODO(v2): Re-enable Remove from Synthpop once we wire the external Synthpop
// notify so cancelling on our side also cancels their queued task. Leaving the
// modal + action creator + BE endpoint in place so v2 only needs to put the
// menu item back. See bulk-actions-toolbar.jsx for the bulk variant.
// import RemoveFromSynthpopModal from "./modals/remove-from-synthpop-modal";
import PostponeModal from "./modals/postpone-modal";

const SYNTHPOP_DISABLED_TITLE =
  "Cannot modify — patient is allocated to Synthpop. Coming in v2.";

const MENU_WIDTH = 220;
const MENU_VERTICAL_GAP = 4;

// Dropdown is rendered via portal to document.body so (a) opening it doesn't
// expand the kebab cell and shift surrounding rows, and (b) it isn't clipped
// by the table-wrap's overflow context for bottom-of-table rows.
const RowActionMenu = ({ row }) => {
  const [activeModal, setActiveModal] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // v1: any admin action on a Synthpop-allocated patient would desync our
  // S3_PatientOutreach state from Synthpop's queued task, so the actions are
  // disabled until v2 wires the external notify.
  const isSynthpopAllocated = row.outreachState === "AllocatedToSynthpop";
  const disabledTitle = isSynthpopAllocated ? SYNTHPOP_DISABLED_TITLE : undefined;

  // Position the dropdown relative to the trigger. useLayoutEffect runs
  // synchronously after DOM mutation so the menu is positioned correctly
  // on its very first paint (no flicker).
  useLayoutEffect(() => {
    if (!showMenu || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = menuRef.current
      ? menuRef.current.offsetHeight
      : 160; // conservative pre-mount fallback
    // Flip above the trigger when there's not enough room below.
    const overflowsBottom =
      rect.bottom + MENU_VERTICAL_GAP + menuHeight > window.innerHeight;
    const top = overflowsBottom
      ? rect.top - menuHeight - MENU_VERTICAL_GAP + window.scrollY
      : rect.bottom + MENU_VERTICAL_GAP + window.scrollY;
    // Right-align with the trigger, keeping the menu inside the viewport.
    const desiredLeft = rect.right - MENU_WIDTH + window.scrollX;
    const left = Math.max(8, desiredLeft);
    setMenuPosition({ top, left });
  }, [showMenu]);

  // Close on outside click or Escape.
  useEffect(() => {
    if (!showMenu) return;
    const handleClick = e => {
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setShowMenu(false);
    };
    const handleKey = e => {
      if (e.key === "Escape") setShowMenu(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [showMenu]);

  const openModal = name => {
    setActiveModal(name);
    setShowMenu(false);
  };
  const closeModal = () => setActiveModal(null);

  const handleItemClick = name => () => {
    if (isSynthpopAllocated) return;
    openModal(name);
  };

  const patientLabel = row.patientName || row.patientGuid;

  return (
    <HasRolePermission allowedRoles={["Administrator"]}>
      <div className="patient-outreach-row-menu">
        <button
          type="button"
          ref={triggerRef}
          className="patient-outreach-row-menu__trigger"
          aria-haspopup="true"
          aria-expanded={showMenu}
          onClick={e => {
            e.stopPropagation();
            setShowMenu(s => !s);
          }}
        >
          <IoMdMenu />
        </button>
        {showMenu &&
          menuPosition &&
          ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="patient-outreach-row-menu__dropdown"
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                width: MENU_WIDTH
              }}
            >
              <div className="patient-outreach-row-menu__header">
                {patientLabel}
              </div>
              <button
                type="button"
                className="patient-outreach-row-menu__item"
                disabled={isSynthpopAllocated}
                title={disabledTitle}
                onClick={handleItemClick("unallocate")}
              >
                Deallocate
              </button>
              {/* TODO(v2): Remove from Synthpop menu item commented out — see import note above. */}
              <button
                type="button"
                className="patient-outreach-row-menu__item"
                disabled={isSynthpopAllocated}
                title={disabledTitle}
                onClick={handleItemClick("postpone")}
              >
                Postpone outreach
              </button>
            </div>,
            document.body
          )}
      </div>
      {activeModal === "unallocate" && (
        <UnallocateModal row={row} onClose={closeModal} />
      )}
      {activeModal === "postpone" && <PostponeModal row={row} onClose={closeModal} />}
    </HasRolePermission>
  );
};

RowActionMenu.propTypes = {
  row: PropTypes.shape({
    patientGuid: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    allocatedCoachName: PropTypes.string,
    outreachState: PropTypes.string
  }).isRequired
};

export default RowActionMenu;
